// Visão por Turma - Bloco 6
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { BookOpen, CheckCircle, AlertTriangle, Clock, Loader2 } from 'lucide-react';

const API_URL = window.location.origin;

export function VisaoTurma({ year, trimestre }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTurma, setSelectedTurma] = useState(null);

  useEffect(() => {
    loadData();
  }, [year, trimestre]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pendencias-academicas/por-turma?year=${year}&trimestre=${encodeURIComponent(trimestre)}`);
      const json = await res.json();
      setData(json);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!data) return null;

  const { turmas, turmas_completas, turmas_atencao, turmas_pendentes } = data;

  return (
    <div className="space-y-6" data-testid="visao-turma">
      {/* Header com Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-80">Completas</div>
                <div className="text-3xl font-bold">{turmas_completas}</div>
              </div>
              <CheckCircle className="w-8 h-8 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-500 to-amber-500 text-white border-0">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-80">Atenção</div>
                <div className="text-3xl font-bold">{turmas_atencao}</div>
              </div>
              <Clock className="w-8 h-8 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-500 to-pink-500 text-white border-0">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-80">Pendentes</div>
                <div className="text-3xl font-bold">{turmas_pendentes}</div>
              </div>
              <AlertTriangle className="w-8 h-8 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-80">Total</div>
                <div className="text-3xl font-bold">{data.total_turmas}</div>
              </div>
              <BookOpen className="w-8 h-8 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Heatmap Grid */}
      <Card className="border-purple-100">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-purple-600" />
            Mapa de Situação por Turma
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {turmas.map((turma, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-xl cursor-pointer transition-all hover:shadow-md hover:scale-105 ${
                  turma.status_heatmap === 'completo' ? 'bg-green-100 border-2 border-green-300' :
                  turma.status_heatmap === 'atencao' ? 'bg-yellow-100 border-2 border-yellow-300' :
                  'bg-red-100 border-2 border-red-300'
                }`}
                onClick={() => setSelectedTurma(selectedTurma?.id === turma.id ? null : turma)}
              >
                <div className="text-center">
                  <div className="font-bold text-slate-800 text-sm truncate">{turma.nome}</div>
                  <div className={`text-2xl font-bold mt-1 ${
                    turma.status_heatmap === 'completo' ? 'text-green-600' :
                    turma.status_heatmap === 'atencao' ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {turma.percentual_conclusao}%
                  </div>
                  {turma.pendencias_criticas > 0 && (
                    <Badge className="bg-red-500 text-white text-xs mt-1">
                      {turma.pendencias_criticas} críticas
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Legenda */}
          <div className="flex justify-center gap-6 mt-6 pt-4 border-t">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-400"></div>
              <span className="text-sm text-slate-600">Completo (≥100%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-400"></div>
              <span className="text-sm text-slate-600">Atenção (70-99%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-400"></div>
              <span className="text-sm text-slate-600">Pendente (&lt;70%)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detalhe da Turma Selecionada */}
      {selectedTurma && (
        <Card className="border-purple-200 bg-purple-50/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Detalhes: {selectedTurma.nome}</span>
              <Badge className={
                selectedTurma.status_heatmap === 'completo' ? 'bg-green-500' :
                selectedTurma.status_heatmap === 'atencao' ? 'bg-yellow-500' : 'bg-red-500'
              }>
                {selectedTurma.percentual_conclusao}% concluído
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Por Disciplina */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-3">Por Disciplina</h4>
                <div className="space-y-2">
                  {selectedTurma.disciplinas.map((disc, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 bg-white rounded-lg">
                      <div className="flex-1">
                        <div className="text-sm font-medium">{disc.nome}</div>
                        <Progress 
                          value={disc.percentual} 
                          className={`h-1.5 mt-1 ${
                            disc.percentual >= 100 ? '[&>div]:bg-green-500' :
                            disc.percentual >= 70 ? '[&>div]:bg-yellow-500' : '[&>div]:bg-red-500'
                          }`}
                        />
                      </div>
                      <span className="text-sm font-medium">{disc.percentual}%</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Por Tipo */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-3">Por Tipo de Avaliação</h4>
                <div className="space-y-2">
                  {selectedTurma.tipos_avaliacao.map((tipo, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg">
                      <span className="text-sm">{tipo.tipo}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-green-600 text-sm">{tipo.lancado} lançados</span>
                        {tipo.pendente > 0 && (
                          <Badge className="bg-red-100 text-red-700 text-xs">{tipo.pendente} pendentes</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
